import { CarrierLogo } from "@/components/carriers/CarrierLogo";
import { WorkflowRail } from "@/components/tms/WorkflowRail";
import { DataTable } from "@/components/table/DataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiClient } from "@/lib/api/client";
import type { CarrierAccount, QuoteRate, RateQuoteRequestLtl, RateQuoteResponse, ShipmentTemplatePayload, CarrierPerformancePayload } from "@/lib/api/types";
import { CARRIER_BY_CODE } from "@/lib/carriers";
import { formatCurrency } from "@/lib/tms/format";
import { DEMO_CARRIER_CODES, ROSSI_DEMO_LANE, ROSSI_DEMO_LANE_SUMMARY } from "@/lib/tms/rossi-demo-lane";
import { isRossiPreviewQuote, isRossiPreviewRate } from "@/lib/tms/rossi-preview";
import { readQuoteDrafts, saveQuoteDraft, saveRecentQuote } from "@/lib/tms/recent";
import { Spinner } from "@/components/ui/spinner";
import { AddressAutocomplete } from "@/components/addresses/AddressAutocomplete";
import { ArrowRight, CalendarDays, ClipboardList, FileSearch2, PlugZap, Save, Sparkles, WandSparkles } from "lucide-react";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const QUOTE_SELECTION_KEY = "shippeek-selected-quote";
const LTL_DEMO_CALL_URL = "https://cal.com/yash-shippeek.com/30min";
const NO_DRAFT = "__no_draft__";
const ROSSI_DEMO_LANE_PRESET = "__rossi_demo_lane__";
const INITIAL_SAMPLE_LANE = {
  originAddress: ROSSI_DEMO_LANE.originAddress,
  originPostalCode: ROSSI_DEMO_LANE.originPostalCode,
  destAddress: ROSSI_DEMO_LANE.destAddress,
  destPostalCode: ROSSI_DEMO_LANE.destPostalCode,
} as const;

const FREIGHT_CLASS_OPTIONS = [
  "50",
  "55",
  "60",
  "65",
  "70",
  "77.5",
  "85",
  "92.5",
  "100",
  "125",
  "150",
  "175",
  "200",
  "250",
  "300",
  "400",
  "500",
];

const HANDLING_UNIT_OPTIONS = ["1", "2", "3", "4", "5", "6", "8", "10"];

/** Map of uppercase carrier code -> performance payload */
type CarrierScoreMap = Map<string, CarrierPerformancePayload>;

export default function QuotesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = searchParams.get("mode") === "parcel" ? "parcel" : "ltl";

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [quote, setQuote] = React.useState<RateQuoteResponse | null>(null);

  // Carrier performance scores (loaded once on mount)
  const [carrierScores, setCarrierScores] = React.useState<CarrierScoreMap>(new Map());
  React.useEffect(() => {
    apiClient.getCarrierScores()
      .then((res) => {
        const map: CarrierScoreMap = new Map();
        for (const s of res.scores) {
          map.set(s.carrierCode.toUpperCase(), s);
        }
        setCarrierScores(map);
      })
      .catch(() => { /* carrier scores are optional - display table without them */ });
  }, []);

  const [connectedCarriers, setConnectedCarriers] = React.useState<CarrierAccount[]>([]);
  const [carriersLoading, setCarriersLoading] = React.useState(true);
  const [carrierCheckFailed, setCarrierCheckFailed] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setCarriersLoading(true);

    apiClient.getCarriers()
      .then((carriers) => {
        if (!cancelled) {
          setConnectedCarriers(carriers);
          setCarrierCheckFailed(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setConnectedCarriers([]);
          setCarrierCheckFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setCarriersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const [originPostalCode, setOriginPostalCode] = React.useState<string>(ROSSI_DEMO_LANE.originPostalCode);
  const [originCity, setOriginCity] = React.useState<string>(ROSSI_DEMO_LANE.originCity);
  const [originState, setOriginState] = React.useState<string>(ROSSI_DEMO_LANE.originState);
  const [originAddress, setOriginAddress] = React.useState<string>(ROSSI_DEMO_LANE.originAddress);
  const [destPostalCode, setDestPostalCode] = React.useState<string>(ROSSI_DEMO_LANE.destPostalCode);
  const [destCity, setDestCity] = React.useState<string>(ROSSI_DEMO_LANE.destCity);
  const [destState, setDestState] = React.useState<string>(ROSSI_DEMO_LANE.destState);
  const [destAddress, setDestAddress] = React.useState<string>(ROSSI_DEMO_LANE.destAddress);
  const [weight, setWeight] = React.useState<string>(ROSSI_DEMO_LANE.weight);
  const [freightClass, setFreightClass] = React.useState<string>(ROSSI_DEMO_LANE.freightClass);
  const [handlingUnits, setHandlingUnits] = React.useState("1");

  // Template state
  const [templates, setTemplates] = React.useState<ShipmentTemplatePayload[]>([]);
  const [templatesLoading, setTemplatesLoading] = React.useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = React.useState(false);
  const [saveTemplateName, setSaveTemplateName] = React.useState("");
  const [savingTemplate, setSavingTemplate] = React.useState(false);
  const [templateMessage, setTemplateMessage] = React.useState<string | null>(null);

  const NO_TEMPLATE = "__no_template__";

  // Load templates from API on mount
  React.useEffect(() => {
    setTemplatesLoading(true);
    apiClient.getTemplates()
      .then((data) => setTemplates(data))
      .catch(() => {/* ignore - templates are optional */})
      .finally(() => setTemplatesLoading(false));
  }, []);

  // Check sessionStorage for a template loaded from the templates page
  React.useEffect(() => {
    const raw = sessionStorage.getItem("shippeek-load-template");
    if (!raw) return;
    sessionStorage.removeItem("shippeek-load-template");
    try {
      const t = JSON.parse(raw) as ShipmentTemplatePayload;
      if (t.mode) setSearchParams(t.mode === "parcel" ? { mode: "parcel" } : {});
      if (t.origin_address) setOriginAddress(t.origin_address);
      if (t.origin_city) setOriginCity(t.origin_city);
      if (t.origin_state) setOriginState(t.origin_state);
      if (t.origin_postal_code) setOriginPostalCode(t.origin_postal_code);
      if (t.dest_address) setDestAddress(t.dest_address);
      if (t.dest_city) setDestCity(t.dest_city);
      if (t.dest_state) setDestState(t.dest_state);
      if (t.dest_postal_code) setDestPostalCode(t.dest_postal_code);
      const item = t.items?.[0];
      if (item) {
        setWeight(String(item.weight));
        setFreightClass(String(item.freightClass));
        setHandlingUnits(String(item.handlingUnits));
      }
    } catch { /* ignore parse errors */ }
  }, [setSearchParams]);

  const applyTemplate = React.useCallback((templateId: string) => {
    const t = templates.find((t) => t.id === templateId);
    if (!t) return;
    setSearchParams(t.mode === "parcel" ? { mode: "parcel" } : {});
    setOriginAddress(t.origin_address);
    setOriginCity(t.origin_city);
    setOriginState(t.origin_state);
    setOriginPostalCode(t.origin_postal_code);
    setDestAddress(t.dest_address);
    setDestCity(t.dest_city);
    setDestState(t.dest_state);
    setDestPostalCode(t.dest_postal_code);
    const item = t.items?.[0];
    if (item) {
      setWeight(String(item.weight));
      setFreightClass(String(item.freightClass));
      setHandlingUnits(String(item.handlingUnits));
    }
  }, [templates, setSearchParams]);

  const handleSaveAsTemplate = async () => {
    if (!saveTemplateName.trim()) return;
    setSavingTemplate(true);
    try {
      const created = await apiClient.createTemplate({
        name: saveTemplateName.trim(),
        mode,
        origin_address: originAddress,
        origin_city: originCity,
        origin_state: originState,
        origin_postal_code: originPostalCode,
        dest_address: destAddress,
        dest_city: destCity,
        dest_state: destState,
        dest_postal_code: destPostalCode,
        items: [{
          handlingUnits: Number(handlingUnits) || 1,
          weight: Number(weight) || 0,
          freightClass: Number(freightClass) || 50,
          pieces: Number(handlingUnits) || 1,
        }],
      });
      setTemplates((prev) => [...prev, created]);
      setSaveDialogOpen(false);
      setSaveTemplateName("");
      setTemplateMessage("Template saved!");
      setTimeout(() => setTemplateMessage(null), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save template.");
    } finally {
      setSavingTemplate(false);
    }
  };

  const [draftOptions, setDraftOptions] = React.useState(() => readQuoteDrafts());
  const [selectedLanePreset, setSelectedLanePreset] = React.useState(ROSSI_DEMO_LANE_PRESET);
  const rossiDemoAutoLoadedRef = React.useRef(false);

  const refreshDrafts = React.useCallback(() => {
    setDraftOptions(readQuoteDrafts());
  }, []);

  const applyDraft = React.useCallback((draftId: string) => {
    const target = readQuoteDrafts().find((item) => item.id === draftId);
    if (!target) return;
    setSelectedLanePreset(draftId);
    setSearchParams(target.mode === "parcel" ? { mode: "parcel" } : {});
    setOriginAddress(target.originAddress);
    setOriginCity(target.originCity);
    setOriginState(target.originState);
    setOriginPostalCode(target.originPostalCode);
    setDestAddress(target.destAddress);
    setDestCity(target.destCity);
    setDestState(target.destState);
    setDestPostalCode(target.destPostalCode);
    setWeight(target.weight);
    setFreightClass(target.freightClass);
    setHandlingUnits(target.handlingUnits);
  }, [setSearchParams]);

  const hasConnectedLtlCarrier = React.useMemo(
    () => connectedCarriers.some(isConnectedLtlCarrier),
    [connectedCarriers],
  );
  const shouldShowLtlCarrierPrompt = mode === "ltl" && !carriersLoading && !carrierCheckFailed && !hasConnectedLtlCarrier;

  const loadRossiDemoLane = React.useCallback(() => {
    setOriginAddress(ROSSI_DEMO_LANE.originAddress);
    setOriginCity(ROSSI_DEMO_LANE.originCity);
    setOriginState(ROSSI_DEMO_LANE.originState);
    setOriginPostalCode(ROSSI_DEMO_LANE.originPostalCode);
    setDestAddress(ROSSI_DEMO_LANE.destAddress);
    setDestCity(ROSSI_DEMO_LANE.destCity);
    setDestState(ROSSI_DEMO_LANE.destState);
    setDestPostalCode(ROSSI_DEMO_LANE.destPostalCode);
    setWeight(ROSSI_DEMO_LANE.weight);
    setFreightClass(ROSSI_DEMO_LANE.freightClass);
    setHandlingUnits(ROSSI_DEMO_LANE.handlingUnits);
    setSelectedLanePreset(ROSSI_DEMO_LANE_PRESET);
  }, []);

  React.useEffect(() => {
    if (mode !== "ltl" || rossiDemoAutoLoadedRef.current) return;

    const isUntouchedInitialSample =
      originAddress === INITIAL_SAMPLE_LANE.originAddress &&
      originPostalCode === INITIAL_SAMPLE_LANE.originPostalCode &&
      destAddress === INITIAL_SAMPLE_LANE.destAddress &&
      destPostalCode === INITIAL_SAMPLE_LANE.destPostalCode;

    if (!isUntouchedInitialSample) return;
    rossiDemoAutoLoadedRef.current = true;
    loadRossiDemoLane();
  }, [destAddress, destPostalCode, loadRossiDemoLane, mode, originAddress, originPostalCode]);

  const submitQuote = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const payload: RateQuoteRequestLtl = {
      originAddress,
      originCity,
      originState,
      originPostalCode,
      destAddress,
      destCity,
      destState,
      destPostalCode,
      items: [
        {
          handlingUnits: Number(handlingUnits),
          weight: Number(weight),
          freightClass: Number(freightClass),
          pieces: Number(handlingUnits),
        },
      ],
    };

    try {
      const next = shouldShowLtlCarrierPrompt
        ? await apiClient.quoteRossiPreview(payload)
        : mode === "parcel"
          ? await apiClient.quoteParcel(payload)
          : await apiClient.quoteLtl(payload);

      if (!next.rates?.length) {
        setQuote(null);
        setError(
          shouldShowLtlCarrierPrompt
            ? `No Rossi preview rates returned for this lane. Use the Texas demo lane (${ROSSI_DEMO_LANE_SUMMARY}) or connect carriers.`
            : "No carrier rates returned for this lane. Check ZIP codes and connected carriers, or try the Rossi demo lane preset.",
        );
        return;
      }

      setQuote(next);
      saveQuoteDraft({
        mode,
        originAddress,
        originCity,
        originState,
        originPostalCode,
        destAddress,
        destCity,
        destState,
        destPostalCode,
        weight,
        freightClass,
        handlingUnits,
      });
      if (!isRossiPreviewQuote(next)) {
        saveRecentQuote({ mode, response: next });
      }
      refreshDrafts();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to fetch rates.");
    } finally {
      setLoading(false);
    }
  };

  const selectForBooking = (rate: QuoteRate) => {
    if (!quote?.id || isRossiPreviewRate(rate)) return;

    localStorage.setItem(
      QUOTE_SELECTION_KEY,
      JSON.stringify({
        mode,
        refId: quote.refId ?? quote.id,
        rateId: rate.ref,
      }),
    );

    navigate("/shipments/book");
  };

  const quoteRef = quote?.refId ?? quote?.id;

  return (
    <div className="space-y-6">
      <WorkflowRail current="quote" />

      <Card>
        <CardHeader className="gap-3 md:flex md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle>{mode === "parcel" ? "Parcel Quote" : "LTL Quote"}</CardTitle>
            <CardDescription>
              {shouldShowLtlCarrierPrompt
                ? "Preview an LTL rate with Rossi, then connect carriers to compare, book, and track."
                : "Get live rates, then push straight into book or retrieve workflows."}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setSaveDialogOpen(true)}>
              <Save className="size-4" />
              Save as Template
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => navigate("/templates")}>
              <ClipboardList className="size-4" />
              Templates
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate("/quotes/retrieve")}
            >
              <FileSearch2 className="size-4" />
              Retrieve Existing
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => navigate("/shipments/book")}>
              Go to Booking
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {shouldShowLtlCarrierPrompt ? (
            <NoLtlCarrierPrompt
              onAddCarrier={() => navigate("/carriers")}
            />
          ) : null}

          <form className="space-y-6" onSubmit={submitQuote}>
            {/* Mode & presets */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5">
                <Label>Mode</Label>
                <Select value={mode} onValueChange={(next) => setSearchParams(next === "parcel" ? { mode: "parcel" } : {})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ltl">LTL</SelectItem>
                    <SelectItem value="parcel">Parcel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {shouldShowLtlCarrierPrompt ? (
                <div className="space-y-1.5 lg:col-span-3">
                  <Label>Lane preset</Label>
                  <Select
                    value={selectedLanePreset}
                    onValueChange={(value) => {
                      if (value === ROSSI_DEMO_LANE_PRESET) {
                        loadRossiDemoLane();
                        return;
                      }
                      if (value === NO_DRAFT) {
                        setSelectedLanePreset(NO_DRAFT);
                        return;
                      }
                      applyDraft(value);
                    }}
                  >
                    <SelectTrigger className="border-sky-200 bg-sky-50/60 text-sky-950 dark:border-sky-900/60 dark:bg-sky-950/25 dark:text-sky-100">
                      <SelectValue placeholder="Choose a lane preset" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ROSSI_DEMO_LANE_PRESET}>Rossi demo lane — {ROSSI_DEMO_LANE_SUMMARY}</SelectItem>
                      <SelectItem value={NO_DRAFT}>Custom lane</SelectItem>
                      {draftOptions.map((draft) => (
                        <SelectItem key={draft.id} value={draft.id}>
                          {draft.originCity}, {draft.originState} ({draft.originPostalCode}) → {draft.destCity}, {draft.destState} ({draft.destPostalCode})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Rossi demo is preselected for the Texas checkout test lane ({ROSSI_DEMO_LANE_SUMMARY}).
                  </p>
                </div>
              ) : templates.length > 0 ? (
                <div className="space-y-1.5 lg:col-span-3">
                  <Label>Load Template</Label>
                  <Select
                    value={NO_TEMPLATE}
                    onValueChange={(value) => {
                      if (value === NO_TEMPLATE) return;
                      applyTemplate(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Load from saved template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_TEMPLATE}>Choose a template</SelectItem>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.is_favorite ? "\u2605 " : ""}{t.name} — {t.origin_city}, {t.origin_state} → {t.dest_city}, {t.dest_state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-1.5 lg:col-span-3">
                  <Label>Recent lane presets</Label>
                  <Select
                    value={NO_DRAFT}
                    onValueChange={(value) => {
                      if (value === NO_DRAFT) return;
                      applyDraft(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Load from real usage history" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_DRAFT}>Choose a saved preset</SelectItem>
                      {draftOptions.map((draft) => (
                        <SelectItem key={draft.id} value={draft.id}>
                          {draft.originCity}, {draft.originState} ({draft.originPostalCode}) → {draft.destCity}, {draft.destState} ({draft.destPostalCode})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="h-px bg-border" />

            {/* Origin section */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Origin</p>
                <AddressAutocomplete
                  label="From Address Book"
                  onSelect={(fields) => {
                    setOriginAddress(fields.address);
                    setOriginCity(fields.city);
                    setOriginState(fields.state);
                    setOriginPostalCode(fields.zip);
                  }}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1.5">
                  <Label>Address</Label>
                  <Input value={originAddress} onChange={(event) => setOriginAddress(event.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label>City</Label>
                  <Input value={originCity} onChange={(event) => setOriginCity(event.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label>State</Label>
                  <Input value={originState} onChange={(event) => setOriginState(event.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label>ZIP</Label>
                  <Input value={originPostalCode} onChange={(event) => setOriginPostalCode(event.target.value)} required />
                </div>
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Destination section */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Destination</p>
                <AddressAutocomplete
                  label="From Address Book"
                  onSelect={(fields) => {
                    setDestAddress(fields.address);
                    setDestCity(fields.city);
                    setDestState(fields.state);
                    setDestPostalCode(fields.zip);
                  }}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1.5">
                  <Label>Address</Label>
                  <Input value={destAddress} onChange={(event) => setDestAddress(event.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label>City</Label>
                  <Input value={destCity} onChange={(event) => setDestCity(event.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label>State</Label>
                  <Input value={destState} onChange={(event) => setDestState(event.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label>ZIP</Label>
                  <Input value={destPostalCode} onChange={(event) => setDestPostalCode(event.target.value)} required />
                </div>
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Freight details section */}
            <div className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Freight Details</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Weight</Label>
                  <Input value={weight} onChange={(event) => setWeight(event.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label>Freight Class</Label>
                  <Select value={freightClass} onValueChange={setFreightClass}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREIGHT_CLASS_OPTIONS.map((value) => (
                        <SelectItem key={value} value={value}>
                          {value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Handling Units</Label>
                  <Select value={handlingUnits} onValueChange={setHandlingUnits}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HANDLING_UNIT_OPTIONS.map((value) => (
                        <SelectItem key={value} value={value}>
                          {value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            {templateMessage ? <p className="text-sm text-emerald-600">{templateMessage}</p> : null}

            <Button type="submit" disabled={loading || (mode === "ltl" && carriersLoading)}>
              {mode === "ltl" && carriersLoading ? (
                <>
                  <Spinner size="sm" className="text-primary-foreground" />
                  Checking carriers...
                </>
              ) : loading ? (
                <>
                  <Spinner size="sm" className="text-primary-foreground" />
                  Fetching rates...
                </>
              ) : shouldShowLtlCarrierPrompt ? (
                <>
                  <WandSparkles className="size-4" />
                  Get Rossi Quote Preview
                </>
              ) : (
                <>
                  <WandSparkles className="size-4" />
                  Get Rates
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {quote
        ? renderQuoteResultsCard({
            quote,
            quoteRef,
            carrierScores,
            openRetrieve: () => navigate(`/quotes/retrieve?ref=${encodeURIComponent(quoteRef ?? "")}&mode=${mode}`),
            openBooking: () => navigate("/shipments/book"),
            openCarrierSetup: () => navigate("/carriers"),
            selectForBooking,
          })
        : null}

      {/* Save as Template dialog */}
      {saveDialogOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Save as Template">
          <div className="mx-4 w-full max-w-md rounded-lg border-[0.5px] border-border bg-card p-6">
            <h2 className="mb-4 text-lg font-semibold">Save as Template</h2>
            <p className="mb-3 text-sm text-muted-foreground">
              Save the current form as a reusable template for one-click quoting.
            </p>
            <div className="mb-4 space-y-1.5">
              <Label>Template Name</Label>
              <Input
                value={saveTemplateName}
                onChange={(e) => setSaveTemplateName(e.target.value)}
                placeholder="e.g. Dallas to Austin Standard"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); void handleSaveAsTemplate(); }
                  if (e.key === "Escape") setSaveDialogOpen(false);
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setSaveDialogOpen(false)} disabled={savingTemplate}>
                Cancel
              </Button>
              <Button type="button" onClick={() => void handleSaveAsTemplate()} disabled={savingTemplate || !saveTemplateName.trim()}>
                {savingTemplate ? (
                  <>
                    <Spinner size="sm" className="text-primary-foreground" />
                    Saving...
                  </>
                ) : (
                  "Save Template"
                )}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function isConnectedLtlCarrier(carrier: CarrierAccount): boolean {
  if (!carrier.isActive || !carrier.hasCredentials) return false;

  const carrierCode = carrier.code.trim().toUpperCase();
  if (DEMO_CARRIER_CODES.has(carrierCode)) return false;
  const definition = CARRIER_BY_CODE.get(carrierCode);

  // If Shippeek receives a custom/unknown connected carrier from the API, treat it
  // as quote-capable instead of blocking a customer who already connected it.
  if (!definition) return true;

  return definition.type === "ltl" || definition.type === "both";
}

function NoLtlCarrierPrompt({ onAddCarrier }: { onAddCarrier: () => void }) {
  return (
    <div className="mb-6 rounded-xl border-[0.5px] border-sky-200 bg-sky-50/70 p-4 text-sky-950 dark:border-sky-900/60 dark:bg-sky-950/25 dark:text-sky-100">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-200">
            <Sparkles className="size-4" />
          </span>
          <div className="space-y-1">
            <p className="font-semibold">Rossi demo lane is ready</p>
            <p className="max-w-2xl text-sm text-sky-900/80 dark:text-sky-200/80">
              We preselected the Texas demo lane so you can preview a Rossi rate immediately.
            </p>
            <p className="text-xs text-sky-800/75 dark:text-sky-200/70">
              Selected preset: {ROSSI_DEMO_LANE_SUMMARY}.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <span className="inline-flex h-8 items-center gap-1.5 rounded-lg border-[0.5px] border-sky-200 bg-white/70 px-3 text-xs font-medium text-sky-900 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-100">
            <WandSparkles className="size-3.5" />
            Rossi demo selected
          </span>
          <Button type="button" size="sm" variant="outline" onClick={onAddCarrier}>
            <PlugZap className="size-4" />
            Connect carriers
          </Button>
          <Button asChild type="button" size="sm" variant="outline">
            <a href={LTL_DEMO_CALL_URL} target="_blank" rel="noreferrer">
              <CalendarDays className="size-4" />
              Talk to us about Rossi
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}

type QuoteResultsCardProps = {
  quote: RateQuoteResponse;
  quoteRef: string | undefined;
  carrierScores: CarrierScoreMap;
  openRetrieve: () => void;
  openBooking: () => void;
  openCarrierSetup: () => void;
  selectForBooking: (rate: QuoteRate) => void;
};

/** Find the carrier code with the highest overall score among the rates */
function findRecommendedCarrierCode(rates: QuoteRate[], scores: CarrierScoreMap): string | null {
  let bestCode: string | null = null;
  let bestScore = -1;

  for (const rate of rates) {
    const s = scores.get(rate.carrierCode.toUpperCase());
    if (s && s.totalShipments >= 3 && s.overallScore > bestScore) {
      bestScore = s.overallScore;
      bestCode = rate.carrierCode.toUpperCase();
    }
  }

  return bestCode;
}

/** Color class for carrier score */
function scoreColorClass(reviewScore: number): string {
  if (reviewScore >= 4.0) return "text-emerald-600";
  if (reviewScore >= 3.0) return "text-yellow-600";
  return "text-red-600";
}

/** Compute industry benchmark percentile based on overall score */
function benchmarkLabel(overallScore: number): string {
  if (overallScore >= 90) return "Better than 95% of carriers";
  if (overallScore >= 80) return "Better than 80% of carriers";
  if (overallScore >= 70) return "Better than 65% of carriers";
  if (overallScore >= 60) return "Better than 50% of carriers";
  if (overallScore >= 50) return "Average performance";
  return "Below average";
}

/** Build human-readable explanation for why a carrier is recommended */
function buildRecommendationExplanation(score: CarrierPerformancePayload, avgTotal: number, rateTotal: number): string {
  const parts: string[] = [];

  if (score.onTimePercent >= 90) {
    parts.push(`${score.onTimePercent}% on-time`);
  }
  if (score.damagePercent <= 1) {
    parts.push("low damage rate");
  }
  if (avgTotal > 0 && rateTotal < avgTotal * 0.95) {
    const savings = Math.round(avgTotal - rateTotal);
    if (savings > 0) parts.push(`$${savings} cheaper than avg`);
  }
  if (score.reviewScore >= 4.0) {
    parts.push(`${score.reviewScore.toFixed(1)}/5 rating`);
  }

  return parts.length > 0 ? `Recommended: ${parts.join(", ")}` : `Score: ${score.overallScore}/100`;
}

function CarrierScoreCell({ carrierCode, scores, isRecommended, rates }: {
  carrierCode: string;
  scores: CarrierScoreMap;
  isRecommended: boolean;
  rates: QuoteRate[];
}) {
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const score = scores.get(carrierCode.toUpperCase());

  if (!score || score.totalShipments < 3) {
    return <span className="text-xs text-muted-foreground">No data</span>;
  }

  const colorClass = scoreColorClass(score.reviewScore);
  const avgTotal = rates.length > 0 ? rates.reduce((sum, r) => sum + r.total, 0) / rates.length : 0;
  const currentRate = rates.find((r) => r.carrierCode.toUpperCase() === carrierCode.toUpperCase());

  return (
    <div className="relative inline-block">
      <button
        type="button"
        className={`flex items-center gap-1 text-sm font-medium ${colorClass} cursor-default`}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
        onFocus={() => setTooltipOpen(true)}
        onBlur={() => setTooltipOpen(false)}
        aria-label={`Carrier score: ${score.reviewScore} out of 5, ${score.onTimePercent}% on-time`}
      >
        <span aria-hidden="true">{"\u2b50"}</span>
        <span>{score.reviewScore.toFixed(1)}/5</span>
        <span className="text-xs text-muted-foreground">({score.onTimePercent}%)</span>
      </button>

      {tooltipOpen ? (
        <div
          className="absolute bottom-full left-1/2 z-50 mb-2 w-60 -translate-x-1/2 rounded-lg border-[0.5px] border-border bg-card p-3 text-sm shadow-md"
          role="tooltip"
        >
          <div className="mb-2 font-semibold">{score.carrierName}</div>
          {isRecommended ? (
            <div className="mb-2 text-xs text-violet-700 dark:text-violet-300">
              {buildRecommendationExplanation(score, avgTotal, currentRate?.total ?? 0)}
            </div>
          ) : null}
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">On-time delivery</span>
              <span className="font-medium">{score.onTimePercent}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Damage rate</span>
              <span className="font-medium">{score.damagePercent}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Review score</span>
              <span className="font-medium">{score.reviewScore.toFixed(1)}/5</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Overall score</span>
              <span className="font-medium">{score.overallScore}/100</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Based on</span>
              <span className="font-medium">{score.totalShipments} shipments</span>
            </div>
          </div>
          <div className={`mt-2 rounded px-2 py-1 text-center text-xs font-medium ${score.overallScore >= 70 ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" : score.overallScore >= 50 ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300" : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"}`}>
            {benchmarkLabel(score.overallScore)}
          </div>
          <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-popover" />
        </div>
      ) : null}
    </div>
  );
}

function renderQuoteResultsCard({
  quote,
  quoteRef,
  carrierScores,
  openRetrieve,
  openBooking,
  openCarrierSetup,
  selectForBooking,
}: QuoteResultsCardProps) {
  const rates = quote.rates ?? [];
  const isRossiPreview = isRossiPreviewQuote(quote);
  const recommendedCode = isRossiPreview ? null : findRecommendedCarrierCode(rates, carrierScores);
  const hasScores = !isRossiPreview && carrierScores.size > 0;

  return (
    <Card>
      <CardHeader className="gap-3 md:flex md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle>{isRossiPreview ? "Rossi Quote Preview" : "Rate Results"}</CardTitle>
          <CardDescription>Reference: {quoteRef}</CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {hasScores ? (
            <span className="inline-flex items-center gap-1.5 rounded-md bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
              <Sparkles className="size-3" />
              AI-Powered Recommendations
            </span>
          ) : null}
          {isRossiPreview ? (
            <>
              <Button type="button" size="sm" onClick={openCarrierSetup}>
                <PlugZap className="size-4" />
                Connect carriers to compare rates
              </Button>
              <Button asChild type="button" variant="outline" size="sm">
                <a href={LTL_DEMO_CALL_URL} target="_blank" rel="noreferrer">
                  <CalendarDays className="size-4" />
                  Talk to us about Rossi
                </a>
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" size="sm" onClick={openRetrieve}>
                Open in Retrieve
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={openBooking}>
                Book From Existing Ref
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isRossiPreview ? (
          <div className="mb-4 rounded-xl border-[0.5px] border-sky-200 bg-sky-50/70 p-4 text-sky-950 dark:border-sky-900/60 dark:bg-sky-950/25 dark:text-sky-100">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold">Preview rate available with Rossi</p>
                  <Badge variant="outline" className="border-sky-200 bg-sky-100/70 text-sky-800 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-200">Local Carrier</Badge>
                  <Badge variant="outline" className="border-slate-200 bg-white/70 text-slate-700 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200">
                    Quote only
                  </Badge>
                </div>
                <p className="max-w-3xl text-sm text-sky-900/80 dark:text-sky-200/80">
                  Rossi rates are available without connecting a carrier account. Connect your carriers to compare rates, book shipments, and unlock tracking.
                </p>
              </div>
            </div>
          </div>
        ) : null}

        <DataTable
          rows={rates}
          getRowId={(row, index) => `${row.ref}-${row.carrierCode}-${row.serviceType}-${index}`}
          columns={[
            {
              key: "carrier",
              label: "Carrier",
              sortable: true,
              render: (row) => (
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex min-w-[160px] items-center gap-2">
                    <CarrierLogo code={row.carrierCode} name={row.carrier} className="size-8 rounded-md" />
                    <span>{row.carrier}</span>
                  </div>
                  {isRossiPreviewRate(row) ? (
                    <>
                      <Badge variant="outline" className="border-sky-200 bg-sky-100/70 text-sky-800 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-200">Local Carrier</Badge>
                      <Badge variant="outline" className="border-slate-200 bg-white/70 text-slate-700 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200">
                        Quote only
                      </Badge>
                    </>
                  ) : null}
                  {recommendedCode && row.carrierCode.toUpperCase() === recommendedCode ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-800 dark:bg-violet-900/40 dark:text-violet-300"
                      title="AI Recommended - best overall score based on on-time %, damage rate, cost, and reviews"
                    >
                      <Sparkles className="size-2.5" />
                      AI Recommended
                    </span>
                  ) : null}
                </div>
              ),
              getSortValue: (row) => row.carrier,
              filter: { type: "select" },
            },
            {
              key: "service",
              label: "Service",
              sortable: true,
              render: (row) => row.serviceDescription || row.serviceType,
              getSortValue: (row) => row.serviceDescription || row.serviceType,
              filter: { type: "text", placeholder: "Service" },
            },
            {
              key: "days",
              label: "Transit Days",
              sortable: true,
              render: (row) => row.days ?? "-",
              getSortValue: (row) => {
                if (row.days === null) return Number.POSITIVE_INFINITY;
                const numericDays = typeof row.days === "number" ? row.days : Number(row.days);
                return Number.isFinite(numericDays) ? numericDays : Number.POSITIVE_INFINITY;
              },
            },
            {
              key: "total",
              label: "Total",
              sortable: true,
              render: (row) => <span className="tabular-nums">{formatCurrency(row.total)}</span>,
              getSortValue: (row) => {
                const numericTotal = typeof row.total === "number" ? row.total : Number(row.total);
                return Number.isFinite(numericTotal) ? numericTotal : Number.POSITIVE_INFINITY;
              },
            },
            {
              key: "action",
              label: "Next",
              render: (row) => isRossiPreviewRate(row) ? (
                <Button size="sm" variant="outline" disabled>
                  Quote only
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => selectForBooking(row)}>
                  Use for Booking
                  <ArrowRight className="size-4" />
                </Button>
              ),
            },
          ]}
        />
      </CardContent>
    </Card>
  );
}
